import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { setAllCustomers } from "../../../state/reducers/customerSlice";
import { getCustomers } from "../../../services/customer.services";
import { ArrowUpRight } from "lucide-react";

const fixedColors = [
  "#7C3AED", // Purple (Primary)
  "#3B82F6", // Blue
  "#10B981", // Green
  "#F59E0B", // Yellow
  "#EF4444", // Red
  "#EC4899", // Pink
];

const CustomersBreakdown = () => {
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const dispatch = useDispatch();

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchAllCustomers = async () => {
      setIsLoading(true);
      try {
        const response = await getCustomers();
        const allData = response.data.data || [];
        dispatch(setAllCustomers(allData));
        const serviceBreakdown = response.data.service_breakdown;

        // Transform service_breakdown into chart data with fixed colors
        const transformedData = serviceBreakdown.map((item, index) => ({
          name: item.service,
          value: item.percentage,
          count: item.count,
          color: fixedColors[index % fixedColors.length],
        }));

        setChartData(transformedData);
      } catch (err) {
        console.error("Error fetching all customers:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllCustomers();
  }, [dispatch]);

  const renderCustomLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    value,
    name,
  }) => {
    // Don't show labels on very small screens to avoid clutter
    if (window.innerWidth < 640) return null;

    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 1.2;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#374151"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        fontSize={window.innerWidth < 768 ? "10px" : "12px"}
        fontWeight="600"
      >
        {`${value}%`}
      </text>
    );
  };

  const customTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-2 sm:p-3 border border-gray-200 rounded-lg shadow-lg max-w-xs">
          <p className="font-medium text-gray-900 text-sm sm:text-base">
            {payload[0].name}
          </p>
          <p className="text-xs sm:text-sm text-gray-600">
            <span className="font-medium">{payload[0].value}%</span> (
            {payload[0].payload.count} students)
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full max-w-7xl mx-auto">
      <div className="bg-white rounded-lg sm:rounded-xl shadow-sm overflow-hidden border-2 border-purple-400">
        <div className="p-3 sm:p-4 md:p-6">
          {/* Header Section */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6">
            <div className="mb-3 sm:mb-0">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800">
                Students Breakdown
              </h2>
              <p className="text-xs sm:text-sm text-gray-500 mt-1">
                By service type
              </p>
            </div>
            {/* <button className="text-xs sm:text-sm flex items-center text-purple-700 hover:text-purple-900 transition-colors self-start sm:self-auto">
              View Details{" "}
              <ArrowUpRight className="ml-1 h-3 w-3 sm:h-4 sm:w-4" />
            </button> */}
          </div>

          {/* Loading State */}
          {isLoading ? (
            <div className="h-[300px] sm:h-[400px] flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 sm:h-10 sm:w-10 border-t-2 border-b-2 border-purple-500 mx-auto mb-2"></div>
                <p className="text-sm text-gray-500">Loading breakdown...</p>
              </div>
            </div>
          ) : (
            /* Main Content Area */
            <div className="flex flex-col xl:flex-row items-center gap-4 sm:gap-6">
              {/* Chart Section */}
              <div className="w-full xl:w-1/2">
                <div className="h-[280px] sm:h-[350px] md:h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={isMobile ? "45%" : "55%"}
                        outerRadius={isMobile ? "65%" : "75%"}
                        paddingAngle={2}
                        label={renderCustomLabel}
                      >
                        {chartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.color}
                            stroke="white"
                            strokeWidth={1}
                          />
                        ))}
                      </Pie>
                      <Tooltip content={customTooltip} />
                      {/* Show legend only on larger screens to avoid crowding */}
                      {!isMobile && (
                        <Legend
                          layout="horizontal"
                          verticalAlign="bottom"
                          align="center"
                          iconType="circle"
                          iconSize={8}
                          wrapperStyle={{
                            fontSize: "12px",
                            paddingTop: "20px",
                          }}
                          formatter={(value) => (
                            <span className="text-xs sm:text-sm text-gray-700">
                              {value}
                            </span>
                          )}
                        />
                      )}
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Stats/Legend Section */}
              <div className="w-full xl:w-1/2">
                <div className="space-y-2 sm:space-y-3 px-0 sm:px-2 md:px-4">
                  <h3 className="text-sm sm:text-base font-semibold text-gray-700 mb-3 xl:hidden">
                    Service Breakdown
                  </h3>

                  {chartData.map((item, index) => (
                    <div
                      key={index}
                      className="bg-gray-50 rounded-lg shadow-sm p-3 sm:p-4 transition-all duration-200 hover:shadow-md hover:scale-[1.02] border border-gray-100"
                    >
                      <div className="flex items-center gap-3 sm:gap-4">
                        <div className="w-full">
                          {/* Header with color indicator and counts */}
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm sm:text-base font-medium text-gray-800 flex items-center">
                              <span
                                className="inline-block w-3 h-3 sm:w-4 sm:h-4 rounded-full mr-2 sm:mr-3 flex-shrink-0"
                                style={{ backgroundColor: item.color }}
                              ></span>
                              <span className="truncate">{item.name}</span>
                            </span>
                            <div className="text-right flex-shrink-0">
                              <span className="text-xs sm:text-sm font-bold text-gray-900 block">
                                {item.value}%
                              </span>
                              <span className="text-xs text-gray-600">
                                {item.count} students
                              </span>
                            </div>
                          </div>

                          {/* Progress Bar */}
                          <div className="relative w-full bg-gray-200 h-4 sm:h-5 rounded-full overflow-hidden">
                            <div
                              className="absolute h-full rounded-full transition-all duration-500 ease-out flex items-center"
                              style={{
                                width: `${item.value}%`,
                                backgroundColor: item.color,
                              }}
                            >
                              {item.value > 15 && (
                                <span className="text-xs font-bold text-white ml-2 sm:ml-3">
                                  {item.value}%
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Empty State */}
                  {chartData.length === 0 && (
                    <div className="text-center py-8 sm:py-12 text-gray-500">
                      <div className="mb-4">
                        <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
                          <span className="text-2xl">📊</span>
                        </div>
                      </div>
                      <p className="text-sm sm:text-base">
                        No service data available
                      </p>
                      <p className="text-xs sm:text-sm text-gray-400 mt-1">
                        Data will appear once students are enrolled
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomersBreakdown;
